/**
 * Shared studio metrics + quota data layer.
 * Consolidates Firestore listeners previously duplicated in StudioStatsBar,
 * useStudioTopBarPlanPill, and ad-hoc getDocs quota scans.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebase';
import { filterRealCaptureDocs } from '../../constants/captureCollection';
import { isStudioPlaceholderDoc } from '../../constants/studioProjects';
import { FREE_STORAGE_CAP_BYTES, getStorageCapBytesForUserDoc } from '../../constants/uploadLimits';
import { registerStudioFirestoreListener } from '../../utils/studioFirestoreInstrumentation';
import {
  coerceStorageBytes,
  computeStudioDisplayStats,
  computeStudioQuotaCounts,
} from './studioMetricsComputations';
import { clearStudioQuotaCache, setStudioQuotaCache } from './studioQuotaCache';

const STORAGE_KEY_PREFIX = 'starling_studio_stats_';

const StudioMetricsContext = createContext(null);

function getStoredStats(uid) {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${uid}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.totalMB !== 'number' || typeof parsed?.totalFrames !== 'number') {
      return null;
    }
    return {
      totalSplats: typeof parsed.totalSplats === 'number' ? parsed.totalSplats : 0,
      totalMB: parsed.totalMB,
      totalFrames: parsed.totalFrames,
      totalCaptureCount: parsed.totalCaptureCount ?? 0,
      captureStorageBytes: null,
      captureStorageCapBytes: null,
    };
  } catch (_) {}
  return null;
}

function persistStats(uid, stats) {
  try {
    const payload = {
      totalSplats: stats.totalSplats ?? 0,
      totalMB: stats.totalMB ?? 0,
      totalFrames: stats.totalFrames ?? 0,
      totalCaptureCount: stats.totalCaptureCount ?? 0,
    };
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${uid}`, JSON.stringify(payload));
  } catch (_) {}
}

/**
 * @param {{ studioUid: string | null, enabled?: boolean, planStartedAtMs?: number | null, children: React.ReactNode }} props
 */
export function StudioMetricsProvider({ studioUid, enabled = true, planStartedAtMs = null, children }) {
  const [displayStats, setDisplayStats] = useState(() =>
    studioUid ? getStoredStats(studioUid) : null,
  );
  const [metricsCaptureTotal, setMetricsCaptureTotal] = useState(null);
  const [quotaCounts, setQuotaCounts] = useState({
    processingCount: 0,
    completedCount: 0,
    rollingCompletedCount: 0,
  });
  const [allCaptures, setAllCaptures] = useState([]);
  const prevCaptureCountForStorageRef = useRef(null);
  const storageRefreshInFlightRef = useRef(false);

  const active = !!(enabled && studioUid);

  const recomputeFromCaptures = useCallback(
    (captures) => {
      setAllCaptures(captures);
      const display = computeStudioDisplayStats(captures);
      const quota = computeStudioQuotaCounts(captures, { planStartedAtMs });
      setQuotaCounts(quota);
      if (studioUid) {
        setStudioQuotaCache(studioUid, {
          ...quota,
          captures,
          planStartedAtMs,
        });
      }
      setDisplayStats((prev) => {
        const next = {
          ...display,
          captureStorageBytes:
            prev && prev.captureStorageBytes != null ? prev.captureStorageBytes : null,
          captureStorageCapBytes:
            prev && prev.captureStorageCapBytes != null ? prev.captureStorageCapBytes : null,
        };
        persistStats(studioUid, next);
        return next;
      });
    },
    [planStartedAtMs, studioUid],
  );

  /** Studio projects + per-project capture listeners (replaces StudioStatsBar N+1 pattern). */
  useEffect(() => {
    if (!active) {
      setAllCaptures([]);
      setQuotaCounts({ processingCount: 0, completedCount: 0, rollingCompletedCount: 0 });
      return undefined;
    }

    let cancelled = false;
    let releaseStudio = () => {};
    let unsubStudio = () => {};
    const captureUnsubs = [];
    const capturesByProject = new Map();

    const tearDownCaptures = () => {
      while (captureUnsubs.length) {
        const u = captureUnsubs.pop();
        try {
          u();
        } catch (_) {}
      }
      capturesByProject.clear();
    };

    const recompute = () => {
      const all = [];
      capturesByProject.forEach((list) => {
        for (const c of list) all.push(c);
      });
      recomputeFromCaptures(all);
    };

    (async () => {
      try {
        if (auth.currentUser?.uid === studioUid) {
          await auth.currentUser.getIdToken(true);
        }
      } catch (_) {}
      if (cancelled) return;

      releaseStudio = registerStudioFirestoreListener(`studioMetrics:studio:${studioUid}`);
      unsubStudio = onSnapshot(
        collection(db, 'users', studioUid, 'studio'),
        (studioSnap) => {
          tearDownCaptures();
          const projects = studioSnap.docs.filter((d) => !isStudioPlaceholderDoc(d));
          if (projects.length === 0) {
            recompute();
            return;
          }
          for (const p of projects) {
            const pid = p.id;
            capturesByProject.set(pid, []);
            const q = query(
              collection(db, 'users', studioUid, 'studio', pid, 'captures'),
              orderBy('createdAt', 'desc'),
            );
            const releaseCap = registerStudioFirestoreListener(
              `studioMetrics:captures:${studioUid}:${pid}`,
            );
            const unsubCap = onSnapshot(
              q,
              (capSnap) => {
                const list = filterRealCaptureDocs(capSnap.docs).map((d) => ({
                  id: d.id,
                  ...d.data(),
                }));
                capturesByProject.set(pid, list);
                recompute();
              },
              (err) => {
                console.error('StudioMetricsProvider captures listener:', pid, err);
                capturesByProject.set(pid, []);
                recompute();
              },
            );
            captureUnsubs.push(() => {
              releaseCap();
              unsubCap();
            });
          }
        },
        (err) => {
          console.error('StudioMetricsProvider studio listener error:', err);
        },
      );
    })();

    return () => {
      cancelled = true;
      tearDownCaptures();
      releaseStudio();
      unsubStudio();
      clearStudioQuotaCache(studioUid);
    };
  }, [active, recomputeFromCaptures, studioUid]);

  /** billing/metrics — capture totals + storage bytes. */
  useEffect(() => {
    if (!active) {
      setMetricsCaptureTotal(null);
      return undefined;
    }
    let cancelled = false;
    let release = () => {};
    let unsub = () => {};
    (async () => {
      try {
        if (auth.currentUser?.uid === studioUid) {
          await auth.currentUser.getIdToken(true);
        }
      } catch (_) {}
      if (cancelled) return;
      release = registerStudioFirestoreListener(`studioMetrics:billingMetrics:${studioUid}`);
      const usageRef = doc(db, 'users', studioUid, 'billing', 'metrics');
      unsub = onSnapshot(
        usageRef,
        (snap) => {
          const d = snap.exists() ? snap.data() : null;
          const bytes = d ? coerceStorageBytes(d?.storage?.totalStorageBytes) : null;
          const capB = d ? coerceStorageBytes(d?.storage?.capBytes) : null;
          const capBFinal = capB != null && capB > 0 ? capB : null;
          const n = d?.captureTotals?.totalCaptures;
          setMetricsCaptureTotal(n != null ? Number(n) || 0 : null);
          setDisplayStats((prev) => {
            const base = prev ?? getStoredStats(studioUid) ?? {
              totalSplats: 0,
              totalMB: 0,
              totalFrames: 0,
              totalCaptureCount: 0,
              captureStorageCapBytes: null,
            };
            const next = {
              ...base,
              ...(bytes != null ? { captureStorageBytes: bytes } : {}),
              ...(capBFinal != null ? { captureStorageCapBytes: capBFinal } : {}),
            };
            persistStats(studioUid, next);
            return next;
          });
        },
        (err) => {
          console.error('StudioMetricsProvider billing/metrics listener error:', err);
        },
      );
    })();

    return () => {
      cancelled = true;
      release();
      unsub();
    };
  }, [active, studioUid]);

  /** billing/plan storage cap override. */
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let release = () => {};
    let unsub = () => {};
    (async () => {
      try {
        if (auth.currentUser?.uid === studioUid) {
          await auth.currentUser.getIdToken(true);
        }
      } catch (_) {}
      if (cancelled) return;
      release = registerStudioFirestoreListener(`studioMetrics:billingPlan:${studioUid}`);
      const planRef = doc(db, 'users', studioUid, 'billing', 'plan');
      unsub = onSnapshot(
        planRef,
        (snap) => {
          const d = snap.exists() ? snap.data() : null;
          if (!d) return;
          const rawOv = d.storageCapOverrideBytes;
          const ov = typeof rawOv === 'string' ? parseInt(rawOv, 10) : Number(rawOv);
          if (!Number.isFinite(ov) || ov <= 0) return;
          const cap = getStorageCapBytesForUserDoc(d, d?.subscription?.plan);
          if (cancelled || !Number.isFinite(cap) || cap <= 0) return;
          setDisplayStats((prev) => {
            const base = prev ?? getStoredStats(studioUid) ?? {
              totalSplats: 0,
              totalMB: 0,
              totalFrames: 0,
              totalCaptureCount: 0,
              captureStorageCapBytes: null,
            };
            const next = { ...base, captureStorageCapBytes: cap };
            persistStats(studioUid, next);
            return next;
          });
        },
        (err) => {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[StudioMetricsProvider] billing/plan listener:', err);
          }
        },
      );
    })();

    return () => {
      cancelled = true;
      release();
      unsub();
    };
  }, [active, studioUid]);

  /** Prime storage from callable on mount. */
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (auth.currentUser?.uid === studioUid) {
          await auth.currentUser.getIdToken(true);
        }
        if (cancelled) return;
        const fn = httpsCallable(functions, 'getCapturesStorageUsage');
        const res = await fn();
        if (cancelled) return;
        const usedBytes = coerceStorageBytes(res?.data?.usedBytes);
        const capBytesRaw = coerceStorageBytes(res?.data?.capBytes);
        const capBytes = capBytesRaw != null && capBytesRaw > 0 ? capBytesRaw : null;
        if (usedBytes != null || capBytes != null) {
          setDisplayStats((prev) => {
            const base = prev ?? getStoredStats(studioUid) ?? {
              totalSplats: 0,
              totalMB: 0,
              totalFrames: 0,
              totalCaptureCount: 0,
              captureStorageCapBytes: null,
            };
            const next = {
              ...base,
              ...(usedBytes != null ? { captureStorageBytes: usedBytes } : {}),
              ...(capBytes != null ? { captureStorageCapBytes: capBytes } : {}),
            };
            persistStats(studioUid, next);
            return next;
          });
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[StudioMetricsProvider] getCapturesStorageUsage failed:', e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, studioUid]);

  /** Refresh storage when capture count increases. */
  useEffect(() => {
    if (!active) {
      prevCaptureCountForStorageRef.current = null;
      return undefined;
    }
    const n = Number(displayStats?.totalCaptureCount ?? 0);
    if (!Number.isFinite(n)) return undefined;
    const prev = prevCaptureCountForStorageRef.current;
    prevCaptureCountForStorageRef.current = n;
    if (prev == null || n <= prev || storageRefreshInFlightRef.current) return undefined;

    let cancelled = false;
    (async () => {
      storageRefreshInFlightRef.current = true;
      try {
        if (auth.currentUser?.uid === studioUid) {
          await auth.currentUser.getIdToken(true);
        }
        if (cancelled) return;
        const fn = httpsCallable(functions, 'getCapturesStorageUsage');
        const res = await fn({ refresh: true });
        if (cancelled) return;
        const usedBytes = coerceStorageBytes(res?.data?.usedBytes);
        const capBytesRaw = coerceStorageBytes(res?.data?.capBytes);
        const capBytes = capBytesRaw != null && capBytesRaw > 0 ? capBytesRaw : null;
        if (usedBytes != null || capBytes != null) {
          setDisplayStats((prevState) => {
            const base = prevState ?? getStoredStats(studioUid) ?? {
              totalSplats: 0,
              totalMB: 0,
              totalFrames: 0,
              totalCaptureCount: 0,
              captureStorageCapBytes: null,
            };
            const next = {
              ...base,
              ...(usedBytes != null ? { captureStorageBytes: usedBytes } : {}),
              ...(capBytes != null ? { captureStorageCapBytes: capBytes } : {}),
            };
            persistStats(studioUid, next);
            return next;
          });
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[StudioMetricsProvider] getCapturesStorageUsage(refresh) failed:', e);
        }
      } finally {
        storageRefreshInFlightRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, displayStats?.totalCaptureCount, studioUid]);

  const refreshStorageUsage = useCallback(async () => {
    if (!studioUid) return;
    try {
      if (auth.currentUser?.uid === studioUid) {
        await auth.currentUser.getIdToken(true);
      }
      const fn = httpsCallable(functions, 'getCapturesStorageUsage');
      const res = await fn({ refresh: true });
      const usedBytes = coerceStorageBytes(res?.data?.usedBytes);
      const capBytesRaw = coerceStorageBytes(res?.data?.capBytes);
      const capBytes = capBytesRaw != null && capBytesRaw > 0 ? capBytesRaw : null;
      if (usedBytes != null || capBytes != null) {
        setDisplayStats((prev) => {
          const base = prev ?? getStoredStats(studioUid) ?? {
            totalSplats: 0,
            totalMB: 0,
            totalFrames: 0,
            totalCaptureCount: 0,
            captureStorageCapBytes: null,
          };
          return {
            ...base,
            ...(usedBytes != null ? { captureStorageBytes: usedBytes } : {}),
            ...(capBytes != null ? { captureStorageCapBytes: capBytes } : {}),
          };
        });
      }
    } catch (_) {}
  }, [studioUid]);

  const value = useMemo(
    () => ({
      studioUid,
      active,
      displayStats: displayStats ?? {
        totalSplats: 0,
        totalMB: 0,
        totalFrames: 0,
        totalCaptureCount: 0,
        captureStorageBytes: null,
        captureStorageCapBytes: null,
      },
      defaultStorageCapBytes: FREE_STORAGE_CAP_BYTES,
      quotaCounts,
      metricsCaptureTotal,
      allCaptures,
      refreshStorageUsage,
    }),
    [
      active,
      allCaptures,
      displayStats,
      metricsCaptureTotal,
      quotaCounts,
      refreshStorageUsage,
      studioUid,
    ],
  );

  return (
    <StudioMetricsContext.Provider value={value}>{children}</StudioMetricsContext.Provider>
  );
}

export function useStudioMetrics() {
  return useContext(StudioMetricsContext);
}

export function useStudioMetricsRequired() {
  const ctx = useContext(StudioMetricsContext);
  if (!ctx) {
    throw new Error('useStudioMetricsRequired must be used within StudioMetricsProvider');
  }
  return ctx;
}
